import { useState } from 'react'
import type { FormEvent } from 'react'

import { Button } from '../components/ui/Button'
import { Field, Input } from '../components/ui/Field'
import { ApiError, login, rememberedTenantSlug } from '../lib/api'
import { MfaChallenge } from './MfaChallenge'

export interface LoginPageProps {
  onSignedIn: () => void
}

interface PendingChallenge {
  token: string
  expiresAt?: string
}

export function LoginPage({ onSignedIn }: LoginPageProps) {
  // When set, the password step succeeded but no session exists yet.
  const [challenge, setChallenge] = useState<PendingChallenge | null>(null)
  const [tenantSlug, setTenantSlug] = useState(rememberedTenantSlug)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setFieldErrors({})
    setSubmitting(true)
    try {
      const result = await login(tenantSlug, email, password)

      // Authentication is not finished. No token was issued, so the app must
      // not treat this as a session — it hands off to the challenge screen.
      if (result.mfa_required && result.mfa_challenge_token) {
        setChallenge({
          token: result.mfa_challenge_token,
          expiresAt: result.mfa_challenge_expires_at,
        })
        // Cleared immediately: there is no reason to keep the password in
        // component state while the user reads a code off their phone.
        setPassword('')
        return
      }
      onSignedIn()
    } catch (err) {
      if (err instanceof ApiError) {
        setFieldErrors(err.fieldErrors)
        // The server returns one generic message for every credential
        // failure — no such user, wrong password, and locked account are
        // deliberately indistinguishable, because telling them apart is a
        // user-enumeration oracle. The UI shows it verbatim rather than
        // trying to be more helpful.
        setError(err.problem.detail)
      } else {
        setError('Could not reach the server. Check your connection and try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (challenge) {
    return (
      <MfaChallenge
        challengeToken={challenge.token}
        expiresAt={challenge.expiresAt}
        onVerified={onSignedIn}
        onCancel={() => {
          setChallenge(null)
          setError(null)
        }}
      />
    )
  }

  return (
    <div className="lg-auth">
      <form className="lg-auth__card" onSubmit={handleSubmit} noValidate>
        <div>
          <h1>Sign in</h1>
          <p className="lg-muted" style={{ fontSize: 'var(--lg-text-sm)', marginTop: 'var(--lg-space-1)' }}>
            Ledger ERP
          </p>
        </div>

        {error && (
          // role="alert" so the failure is announced immediately; a visually
          // obvious banner that screen readers never mention leaves those
          // users pressing a button that appears to do nothing.
          <div className="lg-alert" role="alert">
            {error}
          </div>
        )}

        <Field
          label="Organisation"
          required
          error={fieldErrors.tenant_slug}
          hint="The short name your administrator gave you."
        >
          {(props) => (
            <Input
              {...props}
              name="organization"
              // A stable name so a password manager files the credential
              // under the right organisation rather than under the app.
              autoComplete="organization"
              autoFocus={!tenantSlug}
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value.trim().toLowerCase())}
            />
          )}
        </Field>

        <Field label="Work email" required error={fieldErrors.email}>
          {(props) => (
            <Input
              {...props}
              type="email"
              name="email"
              autoComplete="username"
              autoFocus={Boolean(tenantSlug)}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Password"
          required
          error={fieldErrors.password}
          hint="At least 12 characters."
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <Button type="submit" variant="primary" fullWidth loading={submitting}>
          Sign in
        </Button>
      </form>
    </div>
  )
}
